// src/components/fees/FeeGroups.js
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, Skeleton, Chip, Checkbox, FormControlLabel,
  FormGroup, FormControl, InputLabel, Select, MenuItem, IconButton,
  Alert, Divider, InputAdornment, Stack, TablePagination
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { feeAPI, academicAPI } from '../../services/api';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';

const FeeGroups = () => {
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.FEES.GROUPS, ACTIONS.READ);
  const canCreate = can(RESOURCES.FEES.GROUPS, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.FEES.GROUPS, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.FEES.GROUPS, ACTIONS.DELETE);
  const [feeGroups, setFeeGroups] = useState([]);
  const [classes, setClasses] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    term_id: '',
    class_ids: [],
    fee_types: [{ fee_type_id: '', amount: '' }]
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [termFilter, setTermFilter] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(null);

  useEffect(() => {
    if (canRead) {
      fetchFeeGroups();
      fetchClasses();
      fetchFeeTypes();
      fetchTerms();
    }
  }, [canRead]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchFeeGroups(0);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, pageSize]);

  const fetchFeeGroups = async (pageNum = page) => {
    try {
      const params = {
        page: pageNum + 1,
        per_page: pageSize,
        ...(searchTerm && { search: searchTerm }),
      };
      const response = await feeAPI.getGroups(params);
      const groupsData = response.data.data || response.data || [];
      setFeeGroups(Array.isArray(groupsData) ? groupsData : []);
      setTotalPages(response.data.last_page || 1);
      setTotalRecords(response.data.total || groupsData.length);
    } catch (error) {
      console.error('Error fetching fee groups:', error);
      setFeeGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await academicAPI.getClasses();
      setClasses(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchFeeTypes = async () => {
    try {
      const response = await feeAPI.getTypes();
      setFeeTypes(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error('Error fetching fee types:', error);
    }
  };

  const fetchTerms = async () => {
    try {
      const response = await academicAPI.getTerms();
      setTerms(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error('Error fetching terms:', error);
    }
  };

  const handleClassToggle = (classId) => {
    setFormData(prev => ({
      ...prev,
      class_ids: prev.class_ids.includes(classId)
        ? prev.class_ids.filter(id => id !== classId)
        : [...prev.class_ids, classId]
    }));
  };

  const handleAddFeeType = () => {
    setFormData(prev => ({
      ...prev,
      fee_types: [...prev.fee_types, { fee_type_id: '', amount: '' }]
    }));
  };

  const handleRemoveFeeType = (index) => {
    setFormData(prev => ({
      ...prev,
      fee_types: prev.fee_types.filter((_, i) => i !== index)
    }));
  };

  const handleFeeTypeChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      fee_types: prev.fee_types.map((ft, i) => 
        i === index ? { ...ft, [field]: value } : ft
      )
    }));
  };

  const calculateTotal = () => {
    return formData.fee_types.reduce((sum, ft) => {
      const amount = parseFloat(ft.amount) || 0;
      return sum + amount;
    }, 0);
  };

  const handleDeleteGroup = async () => {
    try {
      await feeAPI.deleteGroup(confirmDeleteGroup);
      fetchFeeGroups(page);
      setConfirmDeleteGroup(null);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete fee group');
      setConfirmDeleteGroup(null);
    }
  };

  const handleEdit = (group) => {
    if (group.term?.is_current) {
      alert('Cannot update fee group for a currently running term');
      return;
    }
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      term_id: group.term_id,
      class_ids: group.classes?.map(c => c.id) || [],
      fee_types: group.fee_types?.map(ft => ({
        fee_type_id: ft.id,
        amount: ft.pivot.amount
      })) || [{ fee_type_id: '', amount: '' }]
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.class_ids.length === 0) {
      alert('Please select at least one class');
      return;
    }
    if (formData.fee_types.some(ft => !ft.fee_type_id || !ft.amount)) {
      alert('Please fill all fee types and amounts');
      return;
    }
    
    // Show confirmation for editing
    if (editingGroup) {
      setShowConfirmDialog(true);
      return;
    }
    
    await saveFeeGroup();
  };
  
  const saveFeeGroup = async () => {
    try {
      let response;
      if (editingGroup) {
        response = await feeAPI.updateGroup(editingGroup.id, formData);
      } else {
        response = await feeAPI.createGroup(formData);
      }
      
      // Show success message with allocation info
      if (editingGroup && response.data.allocations_updated) {
        alert(`Fee group updated successfully!\n${response.data.allocations_updated} allocations updated.`);
      }
      
      fetchFeeGroups(editingGroup ? page : 0);
      setFormData({ 
        name: '', 
        description: '', 
        term_id: '',
        class_ids: [], 
        fee_types: [{ fee_type_id: '', amount: '' }] 
      });
      setEditingGroup(null);
      setShowForm(false);
      setShowConfirmDialog(false);
      if (!editingGroup) setPage(0);
    } catch (error) {
      console.error('Error saving fee group:', error);
      alert(error.response?.data?.message || 'Failed to save fee group');
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchFeeGroups(newPage);
  };

  const handlePageSizeChangeEvent = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    handlePageSizeChange(newPageSize);
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      const params = {
        ...(searchTerm && { search: searchTerm }),
        ...(termFilter && { term_id: termFilter })
      };
      const response = await feeAPI.downloadGroupsPdf(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fee_groups_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const filteredGroups = termFilter
    ? feeGroups.filter(group => group.term_id === parseInt(termFilter))
    : feeGroups;

  if (!canRead) return <Alert severity="error">You do not have permission to view fee groups.</Alert>;

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={200} />;
  }

  return (
    <Box>
      <Box mb={4}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom>Fee Groups</Typography>
            <Typography variant="body1" color="text.secondary">
              Create fee groups with multiple fee types and assign to classes
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1} mt={{ xs: 2, md: 0 }}>
              {canCreate && <Button variant="outlined" startIcon={<PdfIcon />} onClick={handleDownloadPdf} disabled={downloading}>{downloading ? 'Downloading...' : 'Download PDF'}</Button>}
              {canCreate && (
                <Button variant={showForm ? "outlined" : "contained"} startIcon={<AddIcon />} onClick={() => setShowForm(!showForm)}>
                  {showForm ? 'Cancel' : 'Add Fee Group'}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={showForm} onClose={() => { setShowForm(false); setEditingGroup(null); }} maxWidth="md" fullWidth>
        <DialogTitle>{editingGroup ? 'Edit Fee Group' : 'Add New Fee Group'}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., S.1 Term 1 Fees 2025"
              required
              margin="normal"
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Optional description"
              multiline
              rows={2}
              margin="normal"
            />

            <FormControl fullWidth required margin="normal">
              <InputLabel>Term</InputLabel>
              <Select
                value={formData.term_id}
                label="Term"
                onChange={(e) => setFormData({...formData, term_id: e.target.value})}
              >
                {terms.map((term) => (
                  <MenuItem key={term.id} value={term.id}>
                    {term.name} {term.year}
                    {term.is_current && <Chip label="Current" size="small" color="primary" sx={{ ml: 1 }} />}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Fee Types & Amounts
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddFeeType}
                >
                  Add Fee Type
                </Button>
              </Box>

              {formData.fee_types.map((feeType, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <Select
                        value={feeType.fee_type_id}
                        label="Fee Type"
                        onChange={(e) => handleFeeTypeChange(index, 'fee_type_id', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">Select Fee Type</MenuItem>
                        {feeTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Amount (UGX)"
                      type="number"
                      value={feeType.amount}
                      onChange={(e) => handleFeeTypeChange(index, 'amount', e.target.value)}
                      required
                      InputProps={{
                        inputProps: { min: 0, step: "0.01" }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    {formData.fee_types.length > 1 && (
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveFeeType(index)}
                        sx={{ mt: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}

              <Paper sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="h6" color="primary.main">
                  Total Amount: UGX {calculateTotal().toLocaleString()}
                </Typography>
              </Paper>
            </Box>

            <Divider sx={{ my: 3 }} />
            
            <FormControl fullWidth margin="normal">
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Select Classes <Chip label={`${formData.class_ids.length} selected`} size="small" sx={{ ml: 1 }} />
              </Typography>
              <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                <FormGroup>
                  {classes.map((cls) => (
                    <FormControlLabel
                      key={cls.id}
                      control={
                        <Checkbox
                          checked={formData.class_ids.includes(cls.id)}
                          onChange={() => handleClassToggle(cls.id)}
                        />
                      }
                      label={`${cls.name} (${cls.level?.name || 'N/A'})`}
                    />
                  ))}
                </FormGroup>
              </Paper>
            </FormControl>

            {formData.term_id && formData.class_ids.length > 0 && formData.fee_types.some(ft => ft.fee_type_id && ft.amount) && (
              <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon />}>
                This fee group will be created with <strong>{formData.fee_types.filter(ft => ft.fee_type_id && ft.amount).length}</strong> fee type(s) 
                totaling <strong>UGX {calculateTotal().toLocaleString()}</strong> for <strong>{formData.class_ids.length}</strong> class(es) 
                in <strong>{terms.find(t => t.id === formData.term_id)?.name} {terms.find(t => t.id === formData.term_id)?.year}</strong>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowForm(false); setEditingGroup(null); }}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={formData.class_ids.length === 0 || !formData.term_id || formData.fee_types.some(ft => !ft.fee_type_id || !ft.amount)}
          >
            {editingGroup ? 'Update' : 'Save'} Fee Group
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDeleteGroup} onClose={() => setConfirmDeleteGroup(null)}>
        <DialogTitle>Delete Fee Group</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            This will permanently delete the fee group. It cannot be deleted if it has existing student allocations.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteGroup(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteGroup}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>Confirm Fee Group Update</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Important:</strong> Updating this fee group will automatically:
            </Typography>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>Update amounts for all existing student allocations</li>
              <li>Remove allocations for students in removed classes</li>
              <li>Add new fee types to existing student allocations</li>
              <li>Remove allocations for deleted fee types</li>
            </ul>
          </Alert>
          <Typography variant="body2">
            Are you sure you want to proceed with these changes?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button onClick={saveFeeGroup} variant="contained" color="primary">
            Yes, Update
          </Button>
        </DialogActions>
      </Dialog>

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Fee Groups List</Typography>
          </Box>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search fee groups..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={termFilter}
                  onChange={(e) => setTermFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Terms</MenuItem>
                  {terms.map((term) => (
                    <MenuItem key={term.id} value={term.id}>
                      {term.name} {term.year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Term</TableCell>
                  <TableCell>Fee Types</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Classes</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        {searchTerm || termFilter ? 'No fee groups found for the selected filters' : 'No fee groups created yet'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredGroups.map((group) => (
                    <TableRow key={group.id} hover>
                      <TableCell><strong>{group.name}</strong></TableCell>
                      <TableCell>{group.description || '—'}</TableCell>
                      <TableCell>
                        {group.term ? (
                          <Chip 
                            label={`${group.term.name} ${group.term.year}`} 
                            size="small"
                            color={group.term.is_current ? "primary" : "default"}
                          />
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        <Box>
                          {group.fee_types?.map(ft => (
                            <Chip 
                              key={ft.id} 
                              label={`${ft.name}: ${parseFloat(ft.pivot.amount).toLocaleString()}`}
                              size="small" 
                              sx={{ m: 0.5 }}
                              variant="outlined"
                            />
                          )) || '—'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          UGX {(group.fee_types?.reduce((sum, ft) => sum + parseFloat(ft.pivot?.amount || 0), 0) || 0).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {group.classes?.map(cls => (
                          <Chip 
                            key={cls.id} 
                            label={cls.name} 
                            size="small" 
                            sx={{ m: 0.5 }}
                          />
                        )) || '—'}
                      </TableCell>
                      <TableCell>{new Date(group.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {canUpdate && (
                          <IconButton size="small" color="primary" onClick={() => handleEdit(group)} title={group.term?.is_current ? "Cannot edit fee group for current term" : "Edit fee group"} disabled={group.term?.is_current}>
                            <EditIcon />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton size="small" color="error" onClick={() => setConfirmDeleteGroup(group.id)} title="Delete fee group">
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalRecords > 0 && (
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handlePageSizeChangeEvent}
              rowsPerPageOptions={[10, 15, 20, 50]}
              labelRowsPerPage="Rows per page"
              showFirstButton={true}
              showLastButton={true}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default FeeGroups;